import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  useClasses,
  useDepartments,
  useHierarchyLevel,
  useSubclasses,
} from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { useAssortmentPlans } from '@/hooks/useAssortmentPlans'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  buildDetailRows,
  buildMetricSeries,
  DC_OPTIONS,
  EVENT_OPTIONS,
  IMPORT_DOMESTIC_OPTIONS,
  METRIC_KEYS,
  METRIC_LABELS,
  PO_OPTIONS,
  SKU_OPTIONS,
  SNAPSHOT_DATE_OPTIONS,
  SOURCE_OPTIONS,
  SUPPLIER_OPTIONS,
  WEEK_LABELS,
  type InboundFlowFilterState,
  type MetricKey,
} from './inboundFlowReportMockData'
import { InboundFlowMetricsTable } from './InboundFlowMetricsTable'
import { InboundFlowChart } from './InboundFlowChart'
import { InboundDetailViewTable } from './InboundDetailViewTable'

const ALL = '__all__'

type ActiveTab = 'inbound-flow-report' | 'inbound-detail-view'

const DESCRIPTIONS: Record<ActiveTab, string> = {
  'inbound-flow-report':
    'Projected shippable inbound inventory expected to be received at distribution centers by fiscal week, based on the selected snapshot.',
  'inbound-detail-view':
    'Detailed breakdown of inbound inventory lines across PO, JDA, and Planned sources, showing order, SKU, estimated DC arrival date, cartons, cost, cubic feet, and weight for the selected snapshot.',
}

const GLOSSARY: Array<{ term: string; description: string }> = [
  {
    term: 'Cartons',
    description: 'number of inbound cartons associated with the projection.',
  },
  {
    term: 'Cubic volume',
    description: 'projected freight volume associated with inbound inventory.',
  },
  {
    term: 'Cost dollars',
    description:
      'projected cost value for inbound inventory during the selected period.',
  },
  {
    term: 'SKU count',
    description: 'number of distinct SKUs included in the inbound projection.',
  },
  {
    term: 'Units',
    description: 'total inbound units associated with the selected period.',
  },
  {
    term: 'Weight',
    description: 'total inbound product weight associated with the projection.',
  },
]

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface IdOption {
  id: string
  label: string
}

function IdSelect({
  value,
  onValueChange,
  options,
  loading,
  disabled,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: IdOption[]
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.id} value={o.id}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export function InboundFlowReportMockup() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('inbound-flow-report')

  const [snapshotDate, setSnapshotDate] = useState<string>('Latest')
  const [season, setSeason] = useState<string | null>(null)
  const [event, setEvent] = useState<string | null>(null)
  const [source, setSource] = useState<string | null>(null)
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)
  const [dc, setDc] = useState<string | null>(null)
  const [importDomestic, setImportDomestic] = useState<string | null>(null)
  const [planId, setPlanId] = useState<string | null>(null)
  const [supplier, setSupplier] = useState<string | null>(null)
  // Detail View only, but hoisted so they persist across tab switches.
  const [fiscalWeek, setFiscalWeek] = useState<string | null>(null)
  const [po, setPo] = useState<string | null>(null)
  const [sku, setSku] = useState<string | null>(null)

  const [metric, setMetric] = useState<MetricKey>('cartons')

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: seasons, isLoading: seasonsLoading } = useSeasons()
  const { data: divisions, isLoading: divisionsLoading } =
    useHierarchyLevel('DIVISION')
  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)
  const { data: plansResponse, isLoading: plansLoading } = useAssortmentPlans({
    pageSize: 100,
  })

  const seasonOptions = (seasons ?? []).map(s => s.name)
  const divisionOptions: IdOption[] = (divisions ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const departmentOptions: IdOption[] = (departments ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const classOptions: IdOption[] = (classes ?? []).map(c => ({
    id: c.id,
    label: `${c.code} - ${c.name}`,
  }))
  const subclassOptions: IdOption[] = (subclasses ?? []).map(s => ({
    id: s.id,
    label: `${s.code} - ${s.name}`,
  }))
  const plans = plansResponse?.plans ?? []
  const planOptions: IdOption[] = plans.map(p => ({ id: p.id, label: p.name }))

  const filters = useMemo<InboundFlowFilterState>(
    () => ({
      snapshotDate,
      season,
      event,
      source,
      divisionId,
      departmentId,
      classId,
      subclassId,
      dc,
      importDomestic,
      planId,
      supplier,
      fiscalWeek,
      po,
      sku,
    }),
    [
      snapshotDate,
      season,
      event,
      source,
      divisionId,
      departmentId,
      classId,
      subclassId,
      dc,
      importDomestic,
      planId,
      supplier,
      fiscalWeek,
      po,
      sku,
    ]
  )

  const metricSeries = useMemo(() => buildMetricSeries(filters), [filters])
  const detailRows = useMemo(() => buildDetailRows(filters), [filters])

  const handleDepartmentChange = (v: string | null) => {
    setDepartmentId(v)
    setClassId(null)
    setSubclassId(null)
  }

  const handleClassChange = (v: string | null) => {
    setClassId(v)
    setSubclassId(null)
  }

  const isDetailView = activeTab === 'inbound-detail-view'

  return (
    <div className="flex gap-4 bg-[#fafafa] p-6">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {/* Top strip */}
        <div>
          <p className="max-w-2xl text-[11px] leading-tight text-muted-foreground">
            {DESCRIPTIONS[activeTab]}
          </p>
        </div>

        {/* Filter grid — 8 columns, columns 3/4/5 (and 6/7/8 in Detail View)
            have a second, stacked filter directly beneath the first. */}
        <div className="grid grid-cols-8 gap-4">
          <FilterField label="Snapshot Date">
            <Select value={snapshotDate} onValueChange={setSnapshotDate}>
              <SelectTrigger className="w-full bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SNAPSHOT_DATE_OPTIONS.map(o => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Season">
            <SimpleSelect
              value={season}
              onValueChange={setSeason}
              options={seasonOptions}
              loading={seasonsLoading}
            />
          </FilterField>
          <div className="flex flex-col gap-2">
            <FilterField label="Event">
              <SimpleSelect
                value={event}
                onValueChange={setEvent}
                options={EVENT_OPTIONS}
              />
            </FilterField>
            <FilterField label="DC">
              <SimpleSelect
                value={dc}
                onValueChange={setDc}
                options={DC_OPTIONS}
                formatOption={formatDistributionCenterName}
              />
            </FilterField>
          </div>
          <div className="flex flex-col gap-2">
            <FilterField label="Source">
              <SimpleSelect
                value={source}
                onValueChange={setSource}
                options={SOURCE_OPTIONS}
              />
            </FilterField>
            <FilterField label="Imp/Dom">
              <SimpleSelect
                value={importDomestic}
                onValueChange={setImportDomestic}
                options={IMPORT_DOMESTIC_OPTIONS}
              />
            </FilterField>
          </div>
          <div className="flex flex-col gap-2">
            <FilterField label="Division">
              <IdSelect
                value={divisionId}
                onValueChange={setDivisionId}
                options={divisionOptions}
                loading={divisionsLoading}
              />
            </FilterField>
            <FilterField label="Plan Name">
              <IdSelect
                value={planId}
                onValueChange={setPlanId}
                options={planOptions}
                loading={plansLoading}
              />
            </FilterField>
          </div>
          <div className="flex flex-col gap-2">
            <FilterField label="Department">
              <IdSelect
                value={departmentId}
                onValueChange={handleDepartmentChange}
                options={departmentOptions}
                loading={departmentsLoading}
              />
            </FilterField>
            {isDetailView && (
              <FilterField label="Fiscal Week">
                <SimpleSelect
                  value={fiscalWeek}
                  onValueChange={setFiscalWeek}
                  options={WEEK_LABELS}
                />
              </FilterField>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <FilterField label="Class">
              <IdSelect
                value={classId}
                onValueChange={handleClassChange}
                options={classOptions}
                loading={classesLoading}
                disabled={!departmentId}
              />
            </FilterField>
            {isDetailView && (
              <FilterField label="PO">
                <SimpleSelect
                  value={po}
                  onValueChange={setPo}
                  options={PO_OPTIONS}
                />
              </FilterField>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <FilterField label="SubClass">
              <IdSelect
                value={subclassId}
                onValueChange={setSubclassId}
                options={subclassOptions}
                loading={subclassesLoading}
                disabled={!classId}
              />
            </FilterField>
            {isDetailView && (
              <FilterField label="SKU">
                <SimpleSelect
                  value={sku}
                  onValueChange={setSku}
                  options={SKU_OPTIONS}
                />
              </FilterField>
            )}
          </div>
        </div>

        {/* Supplier — own row underneath everything, both tabs. */}
        <div className="grid grid-cols-8 gap-4">
          <FilterField label="Supplier">
            <SimpleSelect
              value={supplier}
              onValueChange={setSupplier}
              options={SUPPLIER_OPTIONS}
            />
          </FilterField>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={v => setActiveTab(v as ActiveTab)}
          className="mt-2"
        >
          <TabsList>
            <TabsTrigger value="inbound-flow-report">
              Inbound Flow Report
            </TabsTrigger>
            <TabsTrigger value="inbound-detail-view">
              Inbound Detail View
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="inbound-flow-report"
            className="flex flex-col gap-4"
          >
            <InboundFlowMetricsTable series={metricSeries} />
            <InboundFlowChart
              series={metricSeries[metric]}
              metricLabel={METRIC_LABELS[metric]}
            />
          </TabsContent>

          <TabsContent value="inbound-detail-view">
            {detailRows.length === 0 ? (
              <div className="flex min-h-40 items-center justify-center rounded-lg border border-border bg-white text-sm text-muted-foreground">
                No results for the selected filters.
              </div>
            ) : (
              <InboundDetailViewTable rows={detailRows} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Right panel — Last Updated + glossary (both tabs) + Metric radio
          (Tab 1 only). The reference screenshots show a brand wordmark in
          this slot instead; a "Last Updated" strip is added here for
          consistency with the rest of the app's mockups. */}
      <div className="flex w-64 shrink-0 flex-col gap-4">
        <div className="text-right text-[11px] text-muted-foreground">
          <div>Last Update (TZ UTC-5)</div>
          <div>{lastUpdate}</div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-white p-3 shadow-sm">
          <ul className="space-y-2.5 text-[11px] leading-snug text-foreground">
            {GLOSSARY.map(item => (
              <li key={item.term}>
                <span className="font-semibold">{item.term}: </span>
                <span className="text-muted-foreground">
                  {item.description}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {activeTab === 'inbound-flow-report' && (
          <div className="rounded-lg border border-border bg-white p-3 shadow-sm">
            <p className="mb-2 text-xs font-semibold text-foreground">Metric</p>
            <RadioGroup
              value={metric}
              onValueChange={v => setMetric(v as MetricKey)}
            >
              {METRIC_KEYS.map(key => (
                <div key={key} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={key}
                    id={`metric-${key}`}
                    className="cursor-pointer"
                  />
                  <Label
                    htmlFor={`metric-${key}`}
                    className="cursor-pointer font-normal text-foreground"
                  >
                    {METRIC_LABELS[key]}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        )}
      </div>
    </div>
  )
}
